// ── Emailing a document to the buyer ─────────────────────────────────────────
// The merchant has no mail server of their own, so this goes out through the workspace's noreply@ mailbox
// carrying the MERCHANT's name, with replies pointed back at them. The envelope is shown but not
// editable — a free-text reply address would let anyone borrow the firm's sending reputation.
//
// Lifted out of ClientPortal/index.tsx (owner 2026-11-02, "Consolidated Document Actions Dropdown"), so the staff's Send Email is this
// same window behind the workspace's gates (routes/acctWorkspace.ts). All server access goes through `sendApi`, as MerchantDocApi does
// for the other dialogs here. ⛔ A portal customer's window must not change: `layer` left out draws exactly what it drew, its own
// overlay at 60 with no X and no Esc (sendWindowGolden.ts hashes it state by state, testDocSendStaff asks). Everything else is the
// staff window's, at WORKSPACE_LAYER: the full-screen DocWindow, the preview step, the precheck and the fuller history.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fmtDateTime } from '../../utils/region';
import { ACCENT, TFn, btn, btnGhost, card, inp, lbl } from './shared';
import { DocWindow, useDocWindowGate, WORKSPACE_LAYER } from './shell';

export type SendDocApi = {
  draft: (kind: string, id: string) => Promise<any>;
  send: (kind: string, id: string, body: any) => Promise<any>;
  openPdf: (kind: string, id: string, t: TFn) => Promise<void>;
  /** Staff only: asked at the Send click, because the verdict that opened the window may be minutes old. The sentence why it may not go now, or ''. */
  preflight?: (kind: string, id: string) => Promise<string>;
};

/** The server's own recipient test (routes/portalDocs.ts SEND_TO_RE): a refused POST pages the admins, so the window asks first. */
export const SEND_TO_RE = /^[^@\s]+@[^@\s.]+\.[^@\s]+$/;

/** The key saying what POST …/send would refuse of these three fields, in its order, or ''. */
export function sendPrecheck(to: string, subject: string, body: string): string {
  if (!SEND_TO_RE.test(to.trim())) return 'aw_doc_send_bad_to';
  if (!subject.trim()) return 'aw_doc_send_no_subject';
  if (!body.trim()) return 'aw_doc_send_no_message';
  return '';
}

/**
 * A document's sends, newest first: the recipient, the date and time in the tenant's zone to the minute, the status, the failure's own
 * words and who sent it, on this window and on the document's (CompanyWorkspace/panes/Documents.tsx DocDetail).
 * ⛔ The status is the mail server's answer and nothing more: it accepted the message, or it did not.
 */
export function SendHistory({ rows }: { rows?: any[] }) {
  const { t } = useTranslation();
  if (!rows?.length) return null;
  return (
    <div data-send-history="" style={{ marginTop: 14, fontSize: 12.5, overflowWrap: 'anywhere' }}>
      <b>{t('aw_doc_send_history')}</b>
      {rows.map((s: any, i: number) => {
        const accepted = !!s.ok;
        return (
          <div key={i} data-send-row={accepted ? 'accepted' : 'failed'} style={{ marginTop: 4 }}>
            <span style={{ color: accepted ? ACCENT : '#D0021B', fontWeight: 600 }}>{accepted ? '✓' : '✕'} {t(accepted ? 'aw_doc_send_status_accepted' : 'aw_doc_send_status_failed')}</span>
            {' · '}{s.to_email}{' · '}{fmtDateTime(s.sent_at)}
            {s.sent_by_name ? <>{' · '}{t('aw_doc_send_by', { name: s.sent_by_name })}</> : null}
            {!accepted && s.error ? <div data-send-error="" style={{ color: '#D0021B', marginTop: 1 }}>{s.error}</div> : null}
          </div>
        );
      })}
    </div>
  );
}

export function SendDocModal({ kind, doc, onClose, onSent, sendApi, layer }:
  { kind: string; doc: any; onClose: () => void; onSent: () => void; sendApi: SendDocApi; layer?: number }) {
  const { t } = useTranslation();
  const staff = layer === WORKSPACE_LAYER;
  const [d, setD] = useState<any>(null);
  const [to, setTo] = useState(''); const [subject, setSubject] = useState(''); const [body, setBody] = useState('');
  const [save, setSave] = useState(true);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState(''); const [done, setDone] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  // The form as the draft filled it is the baseline, so only what somebody typed asks before the window closes (staff; the portal's has no such exit).
  const [filled, setFilled] = useState(0);
  const { dirty, close } = useDocWindowGate(JSON.stringify([to, subject, body]), onClose, busy, filled);

  useEffect(() => {
    sendApi.draft(kind, doc.id).then((r: any) => {
      setD(r); setTo(r.to || ''); setSubject(r.subject || ''); setBody(r.body || ''); setFilled(n => n + 1);
    }).catch((e: any) => setErr(e?.message || t('p_send_err_prepare')));
  }, [kind, doc.id]); // eslint-disable-line

  // What was just tried is in the trail now: the history on screen follows it. What is typed stays.
  const rereadHistory = () => sendApi.draft(kind, doc.id).then((r: any) => { if (r?.history) setD((was: any) => ({ ...was, history: r.history })); }).catch(() => { /* the row stands; the next opening reads it */ });

  const send = async () => {
    setBusy(true); setErr('');
    try {
      const why = staff && sendApi.preflight ? await sendApi.preflight(kind, doc.id) : '';
      if (why) { setErr(why); return; }
      const r: any = await sendApi.send(kind, doc.id, { to: to.trim(), subject, body, saveToCustomer: save });
      setDone(r.to); onSent();
      if (staff) await rereadHistory();
    } catch (e: any) {
      setErr(staff ? t('aw_doc_send_not_sent', { reason: e?.message || t('p_send_err_send') }) : (e?.message || t('p_send_err_send')));
      if (staff) await rereadHistory();
    }
    finally { setBusy(false); }
  };

  const toPreview = () => {
    const problem = sendPrecheck(to, subject, body);
    setErr(problem ? t(problem) : '');
    if (!problem) setPreviewing(true);
  };

  // A long address may not widen the staff window at phone width. ⛔ The portal's three spans stay bare: its DOM is hashed (sendWindowGolden.ts).
  const wraps = staff ? { style: { minWidth: 0, overflowWrap: 'anywhere' as const } } : {};
  // Fixed envelope, shown so the merchant knows what the customer will see.
  const envelope = d && (
    <div style={{ background: '#f6f8f9', border: '1px solid #e3e7ea', borderRadius: 9, padding: '11px 14px',
                  fontSize: 13, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 14px', marginBottom: 14 }}>
      <b style={{ color: '#5b6b79' }}>{t('p_send_from')}</b>
      <span {...wraps}>{d.fromName} &lt;{d.fromEmail}&gt;</span>
      <b style={{ color: '#5b6b79' }}>{t('p_send_replyto')}</b>
      <span {...wraps}>{d.replyTo || <span style={{ color: '#D0021B' }}>{t(staff ? 'aw_doc_send_no_reply_to' : 'p_send_no_replyto')}</span>}</span>
      <b style={{ color: '#5b6b79' }}>{t(staff ? 'aw_doc_send_bcc' : 'p_send_bcc')}</b>
      <span {...wraps}>{d.bcc}</span>
      {/* The preview says the rest of what goes out in the same grid. */}
      {staff && previewing && <>
        <b style={{ color: '#5b6b79' }}>{t('aw_doc_send_to')}</b><span data-send-preview-to="" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{to.trim()}</span>
        <b style={{ color: '#5b6b79' }}>{t('p_send_subject')}</b><span data-send-preview-subject="" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{subject.trim()}</span>
      </>}
    </div>
  );

  // Clickable: the merchant should read the actual PDF before it goes to their customer,
  // not just see its filename. Server-side render, so it gets the spinner every slow
  // button here gets.
  const attachment = d && (
    <button disabled={pdfBusy}
            onClick={async () => { setPdfBusy(true); try { await sendApi.openPdf(kind, doc.id, t); } finally { setPdfBusy(false); } }}
            style={{ ...inp, display: 'flex', alignItems: 'center', gap: 9, textAlign: 'left',
                     cursor: pdfBusy ? 'progress' : 'pointer', background: '#f6f8f9',
                     borderStyle: 'dashed', color: '#27303B', fontWeight: 600 }}>
      {pdfBusy
        ? <span style={{ width: 13, height: 13, border: '2px solid #cfd8e0', borderTopColor: ACCENT,
                         borderRadius: '50%', display: 'inline-block', animation: 'mfspin .7s linear infinite', flex: 'none' }} />
        : <span aria-hidden style={{ fontSize: 15 }}>📄</span>}
      <span {...(staff ? { 'data-send-attachment': '' } : {})} style={{ flex: 1 }}>{staff ? d.attachment : `${d.docNo}.pdf`}</span>
      <span style={{ fontSize: 12, color: ACCENT, fontWeight: 700 }}>
        {pdfBusy ? t('p_send_preparing') : t('p_send_preview')}
      </span>
    </button>
  );

  const fields = d && (<>
    {envelope}

    {staff && !d.hasCustomerEmail && (
      <div data-send-no-email="" style={{ background: '#FFF7E6', border: '1px solid #F2D58A', borderRadius: 9, padding: '9px 12px', fontSize: 13, color: '#7A5B00', marginBottom: 4 }}>
        {t('aw_doc_send_no_customer_email', { customerName: d.customerName })}
      </div>
    )}
    <label style={lbl}>{t('p_send_to_label')}</label>
    <input style={inp} value={to} placeholder={t('p_send_to_ph')} onChange={e => setTo(e.target.value)} />
    {!d.hasCustomerEmail && (
      <label style={{ display: 'flex', gap: 7, alignItems: 'center', marginTop: 7, fontSize: 12.5, cursor: 'pointer' }}>
        <input type="checkbox" checked={save} onChange={e => setSave(e.target.checked)} />
        {t('p_send_save_addr', { customerName: d.customerName })}
      </label>
    )}

    <label style={lbl}>{t('p_send_subject')}</label>
    <input style={inp} value={subject} onChange={e => setSubject(e.target.value)} />

    <label style={lbl}>{t('p_send_message')}</label>
    <textarea style={{ ...inp, minHeight: 300, fontFamily: 'inherit', lineHeight: 1.6, resize: 'vertical' }}
              value={body} onChange={e => setBody(e.target.value)} />

    <label style={lbl}>{t('p_send_attachment')}</label>
    {attachment}
    <div style={{ fontSize: 11.5, color: '#98a4b0', marginTop: 5 }}>
      {t(staff ? 'aw_doc_send_attach_hint' : 'p_send_preview_hint')}
    </div>
  </>);

  if (staff) {
    const gone = d?.found === false;
    return (
      <DocWindow title={t('p_send_title', { docNo: doc.doc_no })} subtitle={t('aw_doc_send_subtitle')} onClose={onClose} busy={busy} dirty={dirty && !done} fullscreen layer={layer}
        footer={
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            {done || gone ? <button style={btn} onClick={onClose}>{t('p_send_done')}</button>
              : previewing ? <>
                <button style={btnGhost} disabled={busy} onClick={() => { setPreviewing(false); setErr(''); }}>{t('aw_doc_send_btn_back')}</button>
                <button data-send-go="" style={{ ...btn, opacity: busy ? .5 : 1 }} disabled={busy} onClick={send}>{busy ? t('p_send_sending') : t('p_send_btn')}</button>
              </> : <>
                <button style={btnGhost} onClick={close}>{t('p_btn_cancel')}</button>
                {d && <button data-send-to-preview="" style={{ ...btn, opacity: d.replyTo ? 1 : .5 }} disabled={!d.replyTo} onClick={toPreview}>{t('aw_doc_send_btn_preview')}</button>}
              </>}
          </div>
        }>
        <div data-send-window="" data-send-stage={done ? 'done' : previewing ? 'preview' : 'compose'} style={{ maxWidth: 780, margin: '0 auto' }}>
          {!d && !err && <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_send_preparing')}</div>}
          {gone ? <div data-send-gone="" style={{ fontSize: 13.5 }}>{t('aw_doc_menu_gone')}</div>
            : done ? (
              <div data-send-done="" style={{ background: '#EAF6EF', border: '1px solid #A7D8BC', borderRadius: 9, padding: '12px 14px', fontSize: 14 }}>
                {t('aw_doc_send_accepted_ok', { email: done })}
              </div>
            ) : d && previewing ? (
              <div data-send-preview="">
                <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>{t('aw_doc_send_preview_note')}</div>
                {envelope}
                <label style={lbl}>{t('p_send_message')}</label>
                <div data-send-preview-body="" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', border: '1px solid #e3e7ea', borderRadius: 9, padding: '12px 14px', fontSize: 14, lineHeight: 1.6 }}>{body.trim()}</div>
                <label style={lbl}>{t('p_send_attachment')}</label>
                {attachment}
              </div>
            ) : fields}
          {err && <div data-send-err="" style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          {!gone && <SendHistory rows={d?.history} />}
        </div>
      </DocWindow>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', zIndex: 60, display: 'flex',
                  alignItems: 'flex-start', justifyContent: 'center', overflow: 'auto', padding: '4vh 12px' }}
         onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...card, width: 780, maxWidth: '96vw', maxHeight: '92vh', overflow: 'auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800 }}>{t('p_send_title', { docNo: doc.doc_no })}</div>
        <div style={{ fontSize: 12.5, color: '#7c8894', marginTop: 2, marginBottom: 12 }}>
          {t('p_send_subtitle')}
        </div>

        {!d && !err && <div style={{ color: '#98a4b0', fontSize: 13 }}>{t('p_send_preparing')}</div>}

        {done ? (
          <div>
            <div style={{ background: '#EAF6EF', border: '1px solid #A7D8BC', borderRadius: 9, padding: '12px 14px', fontSize: 14 }}>
              {t('p_send_sent_ok', { email: done })}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button style={btn} onClick={onClose}>{t('p_send_done')}</button>
            </div>
          </div>
        ) : d && (<>
          {fields}

          {!!d.sends?.length && (
            <div style={{ marginTop: 14, fontSize: 12.5, color: '#5b6b79' }}>
              <b>{t('p_send_history')}</b>
              {d.sends.map((s: any, i: number) => (
                <div key={i} style={{ marginTop: 3 }}>
                  {s.ok ? '✓' : <span style={{ color: '#D0021B' }}>✕</span>} {s.to_email} · {fmtDateTime(s.sent_at)}
                </div>
              ))}
            </div>
          )}

          {err && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
            <button style={btnGhost} onClick={onClose}>{t('p_btn_cancel')}</button>
            <button style={{ ...btn, opacity: busy || !to.trim() || !d.replyTo ? .5 : 1 }}
                    disabled={busy || !to.trim() || !d.replyTo} onClick={send}>
              {busy ? t('p_send_sending') : t('p_send_btn')}
            </button>
          </div>
        </>)}
        {err && !d && <div style={{ color: '#D0021B', fontSize: 12.5, marginTop: 10 }}>{err}</div>}
      </div>
    </div>
  );
}
